import Message from '../components/Message';
import React, { useEffect, useState } from 'react';
import { Pencil, Plus, X } from 'lucide-react';
import api from '../api/api';
import { Alert, Confirm, Modal, TableSkeleton } from '../components';
import { PageHeader } from '../components/layout';

const emptyDate = {
  date: '',
  type: 'holiday',
  start_time: '',
  end_time: '',
  reason: ''
};

const emptyForm = {
  name: '',
  timezone: 'Asia/Shanghai',
  default_work_days: [1, 2, 3, 4, 5],
  default_start_time: '10:00',
  default_end_time: '19:00',
  is_active: true,
  dates: []
};

const weekdayOptions = [
  { value: 1, label: '周一' },
  { value: 2, label: '周二' },
  { value: 3, label: '周三' },
  { value: 4, label: '周四' },
  { value: 5, label: '周五' },
  { value: 6, label: '周六' },
  { value: 7, label: '周日' }
];

const CalendarsPage = () => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });

  useEffect(() => {
    fetchCalendars();
  }, []);

  const fetchCalendars = async () => {
    try {
      setLoading(true);
      const response = await api.getCalendars();
      setCalendars(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setAlertState({ isOpen: true, message: '加载工作日历失败: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFormError(null);
  };

  const editCalendar = (calendar) => {
    setEditorOpen(true);
    setFormError(null);
    setEditingId(calendar.id);
    setForm({
      name: calendar.name || '',
      timezone: calendar.timezone || 'Asia/Shanghai',
      default_work_days: calendar.default_work_days || [1, 2, 3, 4, 5],
      default_start_time: calendar.default_start_time || '10:00',
      default_end_time: calendar.default_end_time || '19:00',
      is_active: calendar.is_active !== false,
      dates: calendar.dates || []
    });
  };

  const toggleWeekday = (day) => {
    setForm(prev => {
      const exists = prev.default_work_days.includes(day);
      const nextDays = exists
        ? prev.default_work_days.filter(item => item !== day)
        : [...prev.default_work_days, day].sort((a, b) => a - b);
      return { ...prev, default_work_days: nextDays };
    });
  };

  const updateDateRow = (index, key, value) => {
    setForm(prev => ({
      ...prev,
      dates: prev.dates.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      ))
    }));
  };

  const addDateRow = () => {
    setForm(prev => ({ ...prev, dates: [...prev.dates, emptyDate] }));
  };

  const removeDateRow = (index) => {
    setForm(prev => ({ ...prev, dates: prev.dates.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const saveCalendar = async (event) => {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    if (!form.name.trim()) {
      setFormError('请输入日历名称');
      return;
    }
    const invalidDate = form.dates.find(item => !item.date || !item.type);
    if (invalidDate) {
      setFormError('特殊日期必须填写日期和类型');
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await api.updateCalendar(editingId, form);
      } else {
        await api.createCalendar(form);
      }
      resetForm();
      setEditorOpen(false);
      fetchCalendars();
    } catch (err) {
      setFormError('保存工作日历失败: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteCalendar = (calendar) => {
    setConfirmState({
      isOpen: true,
      message: `确定要删除工作日历 "${calendar.name}" 吗？`,
      onConfirm: async () => {
        try {
          await api.deleteCalendar(calendar.id);
          fetchCalendars();
          if (editingId === calendar.id) resetForm();
        } catch (err) {
          setAlertState({ isOpen: true, message: '删除工作日历失败: ' + err.message });
        }
        setConfirmState({ ...confirmState, isOpen: false });
      }
    });
  };

  return (
    <div className="configuration-list-page calendars-directory-page">
      <Message message={formError} type="error" />
      <PageHeader
        title="工作日历"
        actions={<button type="button" className="btn btn-primary" onClick={() => { resetForm(); setEditorOpen(true); }}><Plus size={16} aria-hidden="true" />新建工作日历</button>}
      />

      <Modal isOpen={editorOpen} title={editingId ? '编辑工作日历' : '新建工作日历'} size="large" className="configuration-editor-dialog" busy={saving}
        onClose={() => { if (!saving) { setEditorOpen(false); resetForm(); } }}>
        <form onSubmit={saveCalendar}>
          <p className="configuration-dialog-description">默认工作时间和特殊日期会用于规则组的延迟触发。</p>
          <fieldset disabled={saving} className="configuration-form-fields">
          <div className="calendar-form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="calendar-name">日历名称 *</label>
              <input id="calendar-name" required className="form-input" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">时区</label>
              <input className="form-input" value={form.timezone} onChange={(e) => setForm(prev => ({ ...prev, timezone: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">开始时间</label>
              <input className="form-input" type="time" value={form.default_start_time} onChange={(e) => setForm(prev => ({ ...prev, default_start_time: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">结束时间</label>
              <input className="form-input" type="time" value={form.default_end_time} onChange={(e) => setForm(prev => ({ ...prev, default_end_time: e.target.value }))} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">默认工作日</label>
            <div className="calendar-weekday-options">
              {weekdayOptions.map(day => (
                <label key={day.value} className="badge badge-default" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.default_work_days.includes(day.value)}
                    onChange={() => toggleWeekday(day.value)}
                    style={{ marginRight: '6px' }}
                  />
                  {day.label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">特殊日期</label>
            <div className="calendar-special-dates">
              {form.dates.map((item, index) => (
                <div key={index} className="calendar-special-date-row">
                  <label className="calendar-date-field">日期
                    <input className="form-input" type="date" required value={item.date} onChange={(e) => updateDateRow(index, 'date', e.target.value)} />
                  </label>
                  <label className="calendar-date-field">类型
                    <select className="form-input" value={item.type} onChange={(e) => updateDateRow(index, 'type', e.target.value)}>
                      <option value="holiday">节假日</option>
                      <option value="workday">调休工作日</option>
                    </select>
                  </label>
                  <label className="calendar-date-field">开始时间（可选）
                    <input className="form-input" type="time" value={item.start_time || ''} onChange={(e) => updateDateRow(index, 'start_time', e.target.value)} />
                  </label>
                  <label className="calendar-date-field">结束时间（可选）
                    <input className="form-input" type="time" value={item.end_time || ''} onChange={(e) => updateDateRow(index, 'end_time', e.target.value)} />
                  </label>
                  <label className="calendar-date-field">原因（可选）
                    <input className="form-input" value={item.reason || ''} onChange={(e) => updateDateRow(index, 'reason', e.target.value)} placeholder="例如：国庆节" />
                  </label>
                  <button type="button" className="btn btn-danger btn-sm calendar-date-remove" onClick={() => removeDateRow(index)}>删除</button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addDateRow} style={{ alignSelf: 'flex-start' }}>添加特殊日期</button>
            </div>
          </div>

          </fieldset>

          <div className="configuration-dialog-actions">
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => { setEditorOpen(false); resetForm(); }}>取消</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '保存中…' : editingId ? '保存修改' : '创建日历'}</button>
          </div>
        </form>
      </Modal>

      <section className="configuration-list-surface calendars-directory-surface">
        <header className="configuration-list-surface-header">
          <div>
            <h2>已配置日历</h2>
            <p>共 {calendars.length} 个工作日历。</p>
          </div>
          <span className="configuration-list-count">{calendars.filter(calendar => calendar.is_active !== false).length} 已启用</span>
        </header>
        <div className="configuration-list-surface-body">
          {loading ? (
            <div className="calendars-table-skeleton"><TableSkeleton rowCount={5} columnCount={5} label="加载工作日历中" /></div>
          ) : (
            <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
              <table className="table calendars-config-table">
                <thead>
                  <tr>
                    <th>名称</th>
                    <th>默认时间</th>
                    <th>特殊日期</th>
                    <th>状态</th>
                    <th style={{ width: '140px' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {calendars.length === 0 && <tr><td colSpan="5"><div className="empty-state">暂无工作日历，点击“新建工作日历”创建。</div></td></tr>}
                  {calendars.map(calendar => (
                    <tr key={calendar.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{calendar.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{calendar.timezone}</div>
                      </td>
                      <td>{calendar.default_start_time} - {calendar.default_end_time}</td>
                      <td>{(calendar.dates || []).length} 天</td>
                      <td><span className="badge badge-default">{calendar.is_active === false ? '已禁用' : '启用'}</span></td>
                      <td>
                        <div className="table-action-icon-list">
                          <button type="button" className="table-action-icon-button" onClick={() => editCalendar(calendar)} aria-label="编辑工作日历" title="编辑工作日历"><Pencil size={17} aria-hidden="true" /></button>
                          <button type="button" className="table-action-icon-button is-danger" onClick={() => deleteCalendar(calendar)} aria-label="删除工作日历" title="删除工作日历"><X size={19} aria-hidden="true" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <Confirm
        isOpen={confirmState.isOpen}
        title="确认操作"
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState({ ...confirmState, isOpen: false })}
      />
      <Alert
        isOpen={alertState.isOpen}
        title="提示"
        message={alertState.message}
        onClose={() => setAlertState({ ...alertState, isOpen: false })}
      />
    </div>
  );
};

export default CalendarsPage;
